import { Currency } from '@sequencehq/api'
import { toMoney } from '@sequencehq/utils'
import type { CellPosition } from '../hooks/useTableSelection'
import { YearMonth } from '../types'

const formatMoney = (value: number | undefined, currency: Currency) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return ''
  }

  return toMoney({
    value: value.toString(),
    currency
  })
}

export const getCellValue = (
  row: any,
  col: number,
  monthlyColumns: YearMonth[],
  currency: Currency
): string => {
  if (col === 0) {
    return row.original.label
  }

  if (col === 1) {
    return formatMoney(row.original.bookedTotal, currency)
  }

  if (col >= 2 && col < 2 + monthlyColumns.length) {
    const month = monthlyColumns[col - 2]
    return formatMoney(row.original.monthlyValues[month], currency)
  }

  if (col === 2 + monthlyColumns.length) {
    return formatMoney(row.original.recognized, currency)
  }

  if (col === 3 + monthlyColumns.length) {
    return formatMoney(row.original.remaining, currency)
  }

  return ''
}

export const getCellProps = (
  rowIndex: number,
  colIndex: number,
  selection: {
    currentPosition: CellPosition
    isCellSelected: (row: number, col: number) => boolean
    getSelectionBorders: (
      row: number,
      col: number
    ) => { top: boolean; right: boolean; bottom: boolean; left: boolean }
    handleCellMouseDown: (row: number, col: number, e: React.MouseEvent) => void
    handleCellMouseEnter: (row: number, col: number) => void
    handleCellClick: (row: number, col: number, e: React.MouseEvent) => void
    handleCellRightClick?: (
      row: number,
      col: number,
      e: React.MouseEvent,
      data: any,
      currency: string
    ) => void
  },
  data?: any,
  currency?: string
): {
  isFocused: boolean
  isSelected: boolean
  selectionBorders: {
    top: boolean
    right: boolean
    bottom: boolean
    left: boolean
  }
  onMouseDown: (e: React.MouseEvent) => void
  onMouseEnter: () => void
  onClick: (e: React.MouseEvent) => void
  onContextMenu: (e: React.MouseEvent) => void
} => {
  const {
    currentPosition,
    isCellSelected,
    getSelectionBorders,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellClick,
    handleCellRightClick
  } = selection

  return {
    isFocused:
      currentPosition.row === rowIndex && currentPosition.col === colIndex,
    isSelected: isCellSelected(rowIndex, colIndex),
    selectionBorders: getSelectionBorders(rowIndex, colIndex),
    onMouseDown: handleCellMouseDown.bind(null, rowIndex, colIndex),
    onMouseEnter: handleCellMouseEnter.bind(null, rowIndex, colIndex),
    onClick: handleCellClick.bind(null, rowIndex, colIndex),
    onContextMenu: (e: React.MouseEvent) => {
      if (handleCellRightClick && data && currency) {
        handleCellRightClick(rowIndex, colIndex, e, data, currency)
      }
    }
  }
}
